import React, { createContext, useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, noop } from 'lodash';
import Styles from './AuthorGroups.css';
import {
  displayError,
  displaySuccess,
  removeAnyAuthorGroupIdFromURL,
  updateAuthorGroupURLWithId
} from './AuthorGroupsUtils';
import { AUTHOR_GROUPS_INIT_STATE, authorGroupsReducer } from './AuthorGroupsReducer';
import AuthorGroupsList from './components/AuthorGroupsList';
import AuthorGroup from './components/AuthorGroup';
import {
  createAuthorGroupInDB,
  deleteAuthorGroupFromDB,
  getAuthorGroupsFromDB,
  updateAuthorGroupInDB
} from './AuthorGroupsData';

export const AuthorGroupsContext = createContext(null);

const DEFAULT_CAMPAIGN = {};

const AuthorGroups = ({
  isReadOnly,
  campaign = DEFAULT_CAMPAIGN,
  authorGroupIDinURL,
  refreshBackboneCollection = noop
}) => {
  const [ editAuthorGroupId, setEditAuthorGroupId ] = useState(authorGroupIDinURL);
  const [ authorGroups, setAuthorGroups ] = useState([]);
  const [ state, dispatch ] = useReducer(authorGroupsReducer, AUTHOR_GROUPS_INIT_STATE);
  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getAuthorGroupsFromDB(campaign.id);
        setAuthorGroups(data);
      }
      catch (error) {
        displayError({id: 'author-groups-data', msg: error.message, error });
      }
    }
    fetchData();

    return () => setAuthorGroups([]);
  }, []);

  try {
    const isDuplicateName = name => !!authorGroups.find(ag => ag.name === name);

    const handleSaveAuthorGroup = (setIsSaving) => {
      const authorGroupToSave = { ...state.selectedAuthorGroup };
      if (isEmpty(authorGroupToSave.id)) {
        const msgId = 'author-group-create';
        if (isDuplicateName(authorGroupToSave.name.trim())) {
          displayError({id: msgId, msg: `The '${authorGroupToSave.name}' already exists.`});
          return;
        }
        setIsSaving(true);
        createAuthorGroupInDB(campaign.id, authorGroupToSave)
          .then(createdAuthorGroup => {
            const newAuthorGroups = [...authorGroups, createdAuthorGroup];
            setAuthorGroups(newAuthorGroups);
            dispatch({
              type: 'set-selected-author-group',
              authorGroup: createdAuthorGroup,
              title: 'EDIT GROUP'
            });
            return { newAuthorGroups, id: createdAuthorGroup.id };
          })
          .then(({ newAuthorGroups, id }) => {
            setEditAuthorGroupId(id);
            refreshBackboneCollection(newAuthorGroups);
            updateAuthorGroupURLWithId(id);
          })
          .then(() => displaySuccess({ id: msgId, msg: 'Your group has been created.'}))
          .catch(error =>
            displayError({id: msgId, msg: 'There was a problem creating your group. Please try again later.', error})
          )
          .finally(() => setIsSaving(false));
      } else {
        const msgId = 'author-group-update';
        setIsSaving(true);
        updateAuthorGroupInDB(campaign.id, authorGroupToSave)
          .then(updatedAuthorGroup => {
            const newAuthorGroups = authorGroups.map(ag => ((ag.id === updatedAuthorGroup.id) ? updatedAuthorGroup : ag));
            setAuthorGroups(newAuthorGroups);
            dispatch({type: 'set-selected-author-group', authorGroup: updatedAuthorGroup}); // keep it in the edit panel
            return { newAuthorGroups, id: updatedAuthorGroup.id };
          })
          .then(({ newAuthorGroups, id }) => {
            setEditAuthorGroupId(id);
            refreshBackboneCollection(newAuthorGroups);
          })
          .then(() => displaySuccess({ id: msgId, msg: 'Your group has been saved.'}))
          .catch(error =>
            displayError({id: msgId, msg: 'There was a problem saving your group. Please try again later.', error})
          )
          .finally(() => setIsSaving(false));
      }
    };

    const handleDeleteAuthorGroup = () => {
      if (state.selectedAuthorGroup.id) {
        const msgId = 'author-group-delete';
        deleteAuthorGroupFromDB(campaign.id, state.selectedAuthorGroup.id)
          .then(() => {
            const newAuthorGroups = authorGroups.filter(ag => ag.id !== state.selectedAuthorGroup.id);
            setAuthorGroups(newAuthorGroups);
            return newAuthorGroups;
          })
          .then(newAuthorGroups => {
            setEditAuthorGroupId();
            refreshBackboneCollection(newAuthorGroups);
            removeAnyAuthorGroupIdFromURL();
          })
          .then(() => displaySuccess({ id: msgId, msg: 'Your group has been deleted.'}))
          .catch(error =>
            displayError({id: msgId, msg: 'There was a problem deleting this group. Please try again later.', error})
          );
      }
      dispatch({type: 'reset'}); // causes only the AuthorGroupsList will be shown
    };

    return (
      <AuthorGroupsContext.Provider value={{state, dispatch}}>
        <div className={Styles.authorGroupsContainer} data-cy="author-groups-container">
          <AuthorGroupsList
            isReadOnly={isReadOnly}
            groupsLimit={campaign.getGroupLimit()}
            authorGroups={authorGroups}
            editAuthorGroupId={editAuthorGroupId}
          />
          {
            state.authorGroupTitle &&
            <AuthorGroup
              isReadOnly={isReadOnly}
              authorsLimit={campaign.getAuthorsLimit()}
              domainsLimit={campaign.getGroupDomainLimit()}
              mediaOutletsLimit={campaign.getGroupMediaOutletLimit()}
              handleSaveAuthorGroup={handleSaveAuthorGroup}
              handleDeleteAuthorGroup={handleDeleteAuthorGroup}
            />
          }
        </div>
      </AuthorGroupsContext.Provider>
    );
  } catch (error) {
    displayError({id: 'author-groups', msg: error.message, error });
  }
};

AuthorGroups.propTypes = {
  isReadOnly: PropTypes.bool,
  campaign: PropTypes.object,
  authorGroupIDinURL: PropTypes.string,
  refreshBackboneCollection: PropTypes.func
};

export default AuthorGroups;
